<script setup lang="ts">
import { BubbleMenu, type Editor } from "@tiptap/vue-3";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { isTextSelection } from "@tiptap/core";
import type { BubbleMenuPluginProps } from "@tiptap/extension-bubble-menu";

const { t } = useI18n()

const props = defineProps<{
  editor: Editor
}>()

defineExpose({
  toggleLink
})

const extensions = computed(() =>
  props.editor
    .extensionManager
    .extensions
    .map(extension => extension.name)
)

const link = ref({
  open: false,
  href: "",
})

function toggleLink() {
  if (props.editor.isActive('link')) {
    props.editor
      .chain()
      .unsetLink()
      .focus()
      .run()
    return
  }
  link.value.open = true
}

function submitLink() {
  props.editor
    .chain()
    .setLink({ href: unref(link.value.href), target: '_blank' })
    .focus()
    .run()
  link.value.open = false
  link.value.href = ""
}

function cancelLink() {
  link.value.open = false
  link.value.href = ""
}

const shouldShow: BubbleMenuPluginProps["shouldShow"] = ({ view, state, from, to }) => {
  const { doc, selection } = state
  const { empty } = selection

  const isNonEmptyTextBlock = doc.textBetween(from, to).length > 0 && isTextSelection(state.selection)
  const hasEditorFocus = view.hasFocus()
  return hasEditorFocus && !empty && isNonEmptyTextBlock && props.editor.isEditable
}
</script>

<template>
  <BubbleMenu
    :editor="props.editor"
    :tippy-options="{ duration: 100, zIndex: 11 }"
    :should-show="shouldShow"
    pluginKey="bubble-mark"
    class="bg-secondary-highlighted w-min rounded flex flex-row shadow"
  >
    <button
      v-if="extensions.includes('bold')"
      :title="t('jc.component.editor.button.bold.title')"
      @click="editor.chain().focus().toggleBold().run()"
      :aria-selected="editor.isActive('bold')"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        icon="bold"
        fixed-width
      />
    </button>
    <button
      v-if="extensions.includes('italic')"
      :title="t('jc.component.editor.button.italic.title')"
      @click="editor.chain().focus().toggleItalic().run()"
      :aria-selected="editor.isActive('italic')"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        icon="italic"
        fixed-width
      />
    </button>
    <button
      v-if="extensions.includes('strike')"
      :title="t('jc.component.editor.button.strikethrough.title')"
      @click="editor.chain().focus().toggleStrike().run()"
      :aria-selected="editor.isActive('strike')"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        icon="strikethrough"
        fixed-width
      />
    </button>
    <button
      v-if="extensions.includes('underline')"
      :title="t('jc.component.editor.button.underline.title')"
      @click="editor.chain().focus().toggleUnderline().run()"
      :aria-selected="editor.isActive('underline')"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        icon="underline"
        fixed-width
      />
    </button>
    <button
      v-if="extensions.includes('subscript')"
      :title="t('jc.component.editor.button.subscript.title')"
      @click="editor.chain().focus().toggleSubscript().run()"
      :aria-selected="editor.isActive('subscript')"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        icon="subscript"
        fixed-width
      />
    </button>
    <button
      v-if="extensions.includes('superscript')"
      :title="t('jc.component.editor.button.superscript.title')"
      @click="editor.chain().focus().toggleSuperscript().run()"
      :aria-selected="editor.isActive('superscript')"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        icon="superscript"
        fixed-width
      />
    </button>
    <button
      v-if="extensions.includes('code')"
      :title="t('jc.component.editor.button.inlineCode.title')"
      @click="props.editor.chain().toggleCode().focus().run()"
      :aria-selected="editor.isActive('inlineCode')"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        icon="code-simple"
        fixed-width
      />
    </button>
    <button
      v-if="extensions.includes('link')"
      :title="t('jc.component.editor.button.link.title')"
      @click="toggleLink"
      :aria-selected="editor.isActive('link')"
      class="p-1.5 text-white aria-selected:text-primary hover:text-primary-highlighted active:text-primary-selected disabled:text-opacity-50"
    >
      <font-awesome-icon
        :icon="editor.isActive('link') ? 'link-simple-slash' : 'link-simple'"
        fixed-width
      />
    </button>
  </BubbleMenu>

  <Modal
    id="link-modal"
    :open="link.open"
    @cancel="cancelLink"
  >
    <ModalHeader>
      <span class="">
        {{ t('jc.component.editor.button.link.input.heading') }}
      </span>
    </ModalHeader>
    <ModalContent>
      <label class="mt-4 w-full flex flex-col justify-center">
        <span class="text-xs">{{ t('jc.component.editor.button.link.input.label') }}</span>
        <input
          ref="input"
          :placeholder="t('jc.component.editor.button.link.input.placeholder')"
          type="url"
          v-model="link.href"
          class="mt-1 block rounded text-gray-900 invalid:[&:not(:focus)]:border-red-500 peer"
        >
        <span class="mt-2 hidden text-xs text-red-500 peer-[&:not(:focus):invalid]:block">
          {{ t('jc.component.editor.button.link.input.invalid') }}
        </span>
      </label>
    </ModalContent>
    <ModalActions>
      <button
        type="submit"
        @click="submitLink"
        :title="t('jc.component.editor.button.link.input.submitButton.title')"
        class="inline-flex gap-2 w-full justify-center rounded bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-highlighted active:bg-primary-selected disabled:opacity-50 mobile:ml-3 mobile:w-auto"
      >
        <font-awesome-icon
          fixed-width
          icon="save"
          size="lg"
        />
        {{ t('jc.component.editor.button.link.input.submitButton.label') }}
      </button>
    </ModalActions>
  </Modal>
</template>

<style scoped lang="postcss">

</style>
